import React from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'


const PrivacyModal = ({
  onClose,
  accept,
  handleReject
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md p-4">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col relative">
        <div className="flex-shrink-0 border-b border-gray-200 dark:border-border-dark px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold dark:text-white">
              Harvest Alert – Data Protection and Privacy Policy
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <IoIosCloseCircleOutline className="h-6 w-6" />
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Version 1.1 | September 2025
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                Informed Consent and Privacy Statement
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Welcome to Harvest Alert, a digital platform developed and managed by Solidaridad West Africa.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Your privacy and the protection of your data are very important to us. This policy explains how we collect, use, store, and share your information, as well as your rights and choices regarding your personal data. By creating an account and using Harvest Alert, you acknowledge that you have read, understood, and consent to the collection and use of your data as outlined in this policy. If you do not agree with these terms, you should not use the platform.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                1. About Harvest Alert
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Harvest Alert is a digital marketplace and traceability platform designed to connect farmers, processors, and agricultural service providers. It enables users to access and offer farm services, facilitate produce aggregation and processing, and enhance traceability within agricultural supply chains.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                The platform supports efficient coordination, transparent transactions, and improved access to inputs and services — helping all actors in the value chain work together more effectively.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Harvest Alert is owned and managed by Solidaridad West Africa, a non-profit organization dedicated to promoting sustainable and inclusive agricultural practices.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                2. Purpose of Data Collection
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Harvest Alert collects and processes information to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>Enable farmers, aggregators, mills, and service providers to use the platform effectively;</li>
                <li>Facilitate traceability and transparency in agricultural supply chains;</li>
                <li>Improve access to digital services, input supplies, and extension support;</li>
                <li>Enhance service delivery and communication among value chain actors; and</li>
                <li>Support educational and capacity-building initiatives aimed at improving farmer knowledge and productivity.</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mt-3">
                From time to time, Solidaridad West Africa may use data shared on the platform to reach registered farmers with educational or advisory content relevant to their crops, regions, or farming practices. Such communication will always comply with applicable data protection laws. Users may choose to opt out of these messages at any time.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                3. Data We Collect
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Depending on how you use Harvest Alert, we may collect the following types of information:
              </p>

              <div className="mb-4">
                <h4 className="text-base font-semibold mb-2 dark:text-white">Personal and Business Information</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                  <li>Name, phone number, and email address;</li>
                  <li>Business name or farm name, address, and GPS location;</li>
                  <li>User type (farmer, processor, or service provider);</li>
                  <li>Role or position;</li>
                  <li>Photos or documents voluntarily uploaded; and</li>
                  <li>Other details necessary to deliver platform services.</li>
                </ul>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold mb-2 dark:text-white">Operational and Farm Data</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                  <li>Farm or business location boundaries and sizes;</li>
                  <li>Crop and production details;</li>
                  <li>Harvest quantities, input usage, and yield records;</li>
                  <li>Transaction and service history between users; and</li>
                  <li>Information entered to support traceability and reporting.</li>
                </ul>
              </div>

              <div className="mb-4">
                <h4 className="text-base font-semibold mb-2 dark:text-white">Usage and Technical Data</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                  <li>Login activity and feature usage;</li>
                  <li>Device and browser information;</li>
                  <li>Access time and session duration; and</li>
                  <li>Interaction patterns that help us improve user experience.</li>
                </ul>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                4. How We Use Your Information
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Solidaridad West Africa collects and uses information to improve agricultural service delivery and ensure effective platform operations. Specifically, we may use data to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
                <li>Facilitate registration, verification, and management of user accounts.</li>
                <li>Enable coordination between farmers, service providers, aggregators, and processors.</li>
                <li>Improve traceability of agricultural produce and supply chain transparency.</li>
                <li>Enhance platform functionality and tailor services to user needs.</li>
                <li>Support project monitoring, reporting, and evaluation activities.</li>
                <li>Conduct data analysis to guide programme design and delivery.</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mt-3">
                We will not sell, rent, or use data for unrelated purposes. Any processing beyond the stated purposes will require additional consent or will be conducted on a lawful basis consistent with data protection regulations.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                5. Data Sharing
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                Solidaridad West Africa does not sell or trade personal data.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                We may share anonymized or aggregated information with trusted partners or donor agencies to support agricultural research, service delivery, or programme reporting; never in a way that identifies you personally.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Personal data may only be shared:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                <li>With your explicit consent;</li>
                <li>When required by law or a court order; or</li>
                <li>With authorized service providers who process data on our behalf under strict confidentiality and security agreements.</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                6. Data Security
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                We apply industry-standard technical and organizational measures to protect your data from unauthorized access, loss, misuse, or alteration. Access to data is restricted to authorized Solidaridad West Africa staff and service providers who are bound by confidentiality and data protection obligations.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                While we maintain secure systems, users should be aware that no internet-based service is completely risk-free. You are encouraged to protect your login details and promptly report any suspicious activity to Solidaridad West Africa.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                7. Data Retention
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                We retain your information only for as long as needed to fulfill the purposes described in this policy or as required by law. Aggregated, anonymized data may be retained for longer periods to support research, reporting, or learning.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                8. Your Rights
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 ml-4">
                <li>Request access to your personal data;</li>
                <li>Correct or update inaccurate details;</li>
                <li>Request deletion or restriction of your data;</li>
                <li>Withdraw your consent to data processing; and</li>
                <li>Lodge a complaint or ask questions about how your data is handled.</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mt-3">
                All requests can be directed to the Data Protection Officer at <a href="mailto:dpo.ghana@solidaridadnetwork.org" className="text-yellowcolor hover:underline">dpo.ghana@solidaridadnetwork.org</a>
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                9. Withdrawal of Consent
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                If you withdraw consent, your account will be deactivated and access to Harvest Alert will be discontinued. You may request that your personal and business data be permanently deleted by contacting the Data Protection Officer.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                10. Policy Updates
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                This policy may be updated periodically to reflect operational, regulatory, or technical changes. Updates will be communicated through the platform or via email, and continued use of the platform after notification will constitute acceptance of the revised policy.
              </p>
            </div>

            <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <h3 className="text-lg font-semibold mb-3 dark:text-white">
                Consent Statement
              </h3>
              <p className="text-gray-700 dark:text-gray-300">
                By continuing to use the Harvest Alert platform, you confirm that you have read, understood, and agree to the terms of this Data Protection and Privacy Policy. You also consent to the collection and use of your information as described above.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 dark:border-border-dark px-6 py-4">
          <div className="flex flex-col gap-3 w-full">
            <button
              className="w-full py-3 text-sm rounded-lg bg-yellowcolor text-black font-medium hover:bg-yellowcolor/90 transition-colors"
              onClick={() => {
                accept()
                onClose()
              }}
            >
              Accept Policy
            </button>

            <button
              className="w-full py-3 rounded-lg border border-yellowcolor text-yellowcolor text-sm font-medium hover:bg-yellowcolor/10 transition-colors"
              onClick={handleReject}
            >
              Reject Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrivacyModal